import React, { useState } from "react";
import { Box } from "Components/Base/Layout/Box";
import { CardMedia } from "Components/Base/Surfaces/Card/CardMedia";
import format from "lib/format";
import * as CONSTANTS from "./constants";

/**
 * Renders an empty box placeholder when no image is available
 * @returns {JSX.Element} Box component with height styling
 */
export const CardNoImage = () => {
  return (
    <Box
      sx={{ height: CONSTANTS.CARD_NO_IMAGE_HEIGHT_THUMBNAIL, scale: "0.9" }}
    />
  );
};

/**
 * Gets the image URL for a course card based on course type and settings
 * @param {Object} course - Course object containing image and type information
 * @param {boolean} isPortalOverview - Whether card is shown in portal overview
 * @returns {string} URL for the course image
 */
export const getImageUrl = (course, isPortalOverview) => {
  if (course.image?.includes("Utils/StreamFile.aspx")) {
    return format.url(`/${course.image}`);
  }

  const contentType =
    course.courseType === CONSTANTS.COURSE_TYPE_ONLINE ||
    course.type === CONSTANTS.PROGRAM_TYPE_ONLINE
      ? "catcourse"
      : "catcourseobject";

  const courseImageId = course.courseId || course.courseObjectId;

  const activityTypes = [
    CONSTANTS.COURSE_TYPE_ACTIVITY,
    CONSTANTS.COURSE_TYPE_COMMON_ACTIVITY,
    CONSTANTS.COURSE_TYPE_NEW_COMMON_ACTIVITY,
  ];

  if (activityTypes.includes(course.courseType)) {
    return format.url(CONSTANTS.PROGRAM_ACTIVITY_IMAGE);
  }

  if (
    course.courseType === CONSTANTS.COURSE_TYPE_SURVEY_TO_COMPLETE ||
    course.type === CONSTANTS.COURSE_TYPE_SURVEY_TO_COMPLETE
  ) {
    return format.url(CONSTANTS.PROGRAM_SURVEY_IMAGE);
  }

  if (course.image) {
    return format.url(
      `/education/luvitcontentfiles/${contentType}/${courseImageId}/${encodeURIComponent(
        course.image,
      )}`,
    );
  }

  return isPortalOverview
    ? format.url(CONSTANTS.PROGRAM_COURSE_IMAGE_PORTALOVERVIEW)
    : format.url(CONSTANTS.PROGRAM_COURSE_IMAGE);
};

/**
 * Gets the URL for a blank placeholder image
 * @param {boolean} isPortalOverview - Whether card is shown in portal overview
 * @returns {string} URL for blank placeholder image
 */
export const getBlankImageUrl = (isPortalOverview) => {
  return isPortalOverview
    ? format.url(CONSTANTS.PROGRAM_COURSE_IMAGE_BLANK_PORTALOVERVIEW)
    : format.url(CONSTANTS.PROGRAM_COURSE_IMAGE_BLANK);
};

/**
 * Custom hook to manage course rating state and updates
 * @param {Object} course - Course object containing rating information
 * @returns {Object} Object containing rating state and update function
 */
export const useRating = (course) => {
  const [rating, setRating] = useState(course.personalRating?.rating || 0);
  const [comment, setComment] = useState(course.personalRating?.comment || "");

  const updateRating = (newRating, newComment) => {
    setRating(newRating);
    setComment(newComment);
  };
  return { rating, comment, updateRating };
};

/**
 * Gets image component and display settings for a course card
 * @param {Object} course - Course object containing image information
 * @param {boolean} displayImage - Whether to display the actual image
 * @param {boolean} isPortalOverview - Whether card is shown in portal overview
 * @param {boolean} hasError - Whether the image has failed to load
 * @param {Function} onError - Error handler function
 * @returns {Object} Object containing display image URL and component
 */
export const getCardImage = (
  course,
  displayImage,
  isPortalOverview,
  size = "medium",
  hasError = false,
  onError = null,
) => {
  const imageUrl = getImageUrl(course, isPortalOverview);
  const blankImgUrl = getBlankImageUrl(isPortalOverview);
  const showImage = displayImage ? imageUrl : blankImgUrl;
  const showImageComponent = imageUrl !== format.url(CONSTANTS.PROGRAM_COURSE_IMAGE_BLANK);
  const imageSx = showImageComponent
    ? {
        height: size === "medium" ? CONSTANTS.CARD_IMAGE_HEIGHT : "85px",
      }
    : {
        height: CONSTANTS.CARD_IMAGE_HEIGHT,
        backgroundColor: "var(--colorCourseCardFallback)",
        scale: "0.9",
      };

  if (hasError) {
    return {
      displayImage: showImage,
      imageComponent: <CardFallbackImage title={course.title} />,
    };
  }

  return {
    displayImage: showImage,
    imageComponent: showImage ? (
      <CardMedia
        image={showImage}
        component="img"
        sx={imageSx}
        alt={course.title}
        onError={onError}
      />
    ) : (
      <CardNoImage />
    ),
  };
};

/**
 * Renders a fallback image when course image is not available
 * @param {string} props.title - Course title to use for accessibility
 * @returns {JSX.Element} Box component styled as fallback image
 */
export const CardFallbackImage = ({ title = "" }) => {
  return (
    <Box
      sx={{
        height: CONSTANTS.CARD_IMAGE_HEIGHT,
        backgroundColor: "var(--colorCourseCardFallback);",
        scale: "0.9",
      }}
      className="card-noimage"
    >
      <span className="sr-only">
        {title && title.length > 1 ? title.substr(0, 2) : title.substr(0, 1)}
      </span>
    </Box>
  );
};

/**
 * Gets formatted title for course card
 * @param {Object} course - Course object containing title and order
 * @param {boolean} isPortalOverview - Whether card is shown in portal overview
 * @returns {string} Formatted course title
 */
export const getCardTitle = (course, isPortalOverview = false) => {
  return isPortalOverview
    ? `${course.title}`
    : `${course.order}) ${course.title}`;
};

/**
 * Formats course duration into readable string
 * @param {Object} course - Course object containing duration information
 * @param {Function} languageContext - Translation function
 * @returns {string} Formatted duration string
 */
export const formatCourseDuration = (course, languageContext) => {
  let time = "";

  if (course.durationHours || course.durationMinutes) {
    if (course.durationHours && course.durationHours !== 0) {
      time = `${course.durationHours} h`;
    }

    if (course.durationMinutes) {
      time += `${time ? " " : ""}${course.durationMinutes} ${languageContext("minutesshort")}`;
    }
  }

  return time;
};

/**
 * Renders time display component if duration is available
 * @param {string} displayTime - Formatted duration string
 * @returns {JSX.Element|null} Time display component or null
 */
export const renderTimeDisplay = (displayTime) => {
  if (displayTime) {
    return (
      <span>
        <span className="icon-time" aria-hidden="true" />
        &nbsp;{displayTime}
      </span>
    );
  }
  return null;
};

/**
 * Gets the appropriate icon class based on course type
 * @param {(string|number)} type - Course type identifier from CONSTANTS or legacy number codes
 * @param {boolean} useProgram - Whether to prefix icon class with 'course-icon-'
 * @returns {string} CSS class name for the icon
 * @private
 */
const getIconClass = (type, useProgram) => {
  const baseIcons = {
    [CONSTANTS.PROGRAM_TYPE_ONLINE]: "online",
    [CONSTANTS.PROGRAM_TYPE_CLASSROOM]: "classroom",
    [CONSTANTS.COURSE_TYPE_ONLINE]: "online",
    [CONSTANTS.COURSE_TYPE_CLASSROOM]: "classroom",
    [CONSTANTS.COURSE_TYPE_PROGRAM]: "program-icon",
    [CONSTANTS.COURSE_TYPE_SURVEY_TO_COMPLETE]: "survey",
    [CONSTANTS.COURSE_TYPE_ACTIVITY]: "activity",
    [CONSTANTS.COURSE_TYPE_COMMON_ACTIVITY]: "activity",
    [CONSTANTS.COURSE_TYPE_NEW_COMMON_ACTIVITY]: "activity",
    1: "online",
    2: "classroom",
  };

  const baseIcon = baseIcons[type] || "default-icon";
  return useProgram ? `course-icon-${baseIcon}` : baseIcon;
};

/**
 * Gets the CSS class name for a program type icon
 * @param {(string|number)} type - Program type identifier from CONSTANTS or legacy number codes
 * @param {boolean} [useProgram=false] - Whether to prefix icon class with 'course-icon-'
 * @returns {string} CSS class name for the program icon
 */
export const getProgramTypeIconClass = (type, useProgram = false) => {
  return getIconClass(type, useProgram);
};
